import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { FaSearch, FaPlus, FaEdit, FaTrash, FaUserShield, FaGraduationCap,
         FaChevronDown, FaChevronUp, FaSave, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import cpApi from '../../utils/cpApi';
import './AddAdmin.css';

const EMPTY_FORM = { name: '', userId: '', password: '' };

const AddAdmin = () => {
  const [users, setUsers]             = useState([]);
  const [colleges, setColleges]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [editId, setEditId]           = useState(null);

  // Expand view — shows hidden column data
  const [expandedInfo, setExpandedInfo] = useState(null);

  // Assignment panel — separate from expand
  const [assignUserId, setAssignUserId]           = useState(null);
  const [tempColleges, setTempColleges]           = useState([]);
  const [originalColleges, setOriginalColleges]   = useState([]);
  const [collegeSearch, setCollegeSearch]         = useState('');
  const [showCollegeDropdown, setShowCollegeDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // ── Load Data ─────────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, cRes] = await Promise.all([
        cpApi.get('/controlpanel/admin', { params: { _t: Date.now() } }),
        cpApi.get('/controlpanel/colleges', { params: { _t: Date.now() } }),
      ]);
      setUsers(uRes.data.data);
      setColleges(cRes.data.data.filter(c => c.isActive));
    } catch {
      // Handle error gracefully
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowCollegeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Form Logic ───────────────────────────────────────────────────────────────
  const handleInputChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const validateForm = () => {
    if (!form.name || !form.userId) {
      Swal.fire('Missing Fields', 'Please fill Name and Admin ID.', 'warning');
      return false;
    }
    if (!editId && !form.password) {
      Swal.fire('Missing Fields', 'Password is required for new admins.', 'warning');
      return false;
    }
    return true;
  };

  const resetForm = () => { setForm(EMPTY_FORM); setEditId(null); setShowAddForm(false); };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = { name: form.name, userId: form.userId };
      if (form.password) payload.password = form.password;
      if (editId) {
        const { data } = await cpApi.put(`/controlpanel/admin/${editId}`, payload);
        setUsers(prev => prev.map(u => u._id === editId ? data.data : u));
        Swal.fire('Updated!', 'Admin updated.', 'success');
      } else {
        const { data } = await cpApi.post('/controlpanel/admin', payload);
        setUsers(prev => [data.data, ...prev]);
        Swal.fire('Added!', 'Admin created.', 'success');
      }
      resetForm();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed.', 'error');
    } finally { setSaving(false); }
  };

  const handleEdit = (user) => {
    setEditId(user._id);
    setForm({ name: user.name, userId: user.userId, password: '' });
    setShowAddForm(true);
    setExpandedInfo(null);
    setAssignUserId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (user) => {
    Swal.fire({
      title: `Delete "${user.name}"?`, text: 'This cannot be undone.',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#e74c3c', cancelButtonColor: '#95a5a6',
      confirmButtonText: 'Yes, delete it',
    }).then(async (r) => {
      if (!r.isConfirmed) return;
      try {
        await cpApi.delete(`/controlpanel/admin/${user._id}`);
        setUsers(prev => prev.filter(u => u._id !== user._id));
        if (expandedInfo === user._id) setExpandedInfo(null);
        if (assignUserId === user._id) setAssignUserId(null);
        Swal.fire('Deleted!', 'Admin deleted.', 'success');
      } catch (err) {
        Swal.fire('Error', err.response?.data?.message || 'Delete failed.', 'error');
      }
    });
  };

  const toggleUserStatus = async (user) => {
    try {
      const { data } = await cpApi.patch(`/controlpanel/admin/${user._id}/toggle`);
      setUsers(prev => prev.map(u => u._id === user._id ? data.data : u));
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed.', 'error');
    }
  };

  const toggleExpandInfo = (userId) => {
    setExpandedInfo(prev => prev === userId ? null : userId);
  };

  // ── Assignment Panel ─────────────────────────────────────────────────────────
  const openAssign = (user) => {
    setAssignUserId(user._id);
    const assigned = (user.colleges || []).map(c => ({ _id: c._id, name: c.name, code: c.code }));
    setTempColleges(assigned);
    setOriginalColleges(assigned);
    setCollegeSearch('');
    setShowCollegeDropdown(false);
    setExpandedInfo(null);
  };

  const closeAssign = async () => {
    const hasChanges =
      JSON.stringify(tempColleges.map(c => c._id).sort()) !==
      JSON.stringify(originalColleges.map(c => c._id).sort());
    if (hasChanges) {
      const r = await Swal.fire({
        title: 'Unsaved Changes',
        text: 'You have unsaved college assignments. Discard them?',
        icon: 'warning', showCancelButton: true,
        confirmButtonColor: '#e74c3c', cancelButtonColor: '#3498db',
        confirmButtonText: 'Yes, discard', cancelButtonText: 'Keep editing',
      });
      if (!r.isConfirmed) return;
    }
    setAssignUserId(null);
    setTempColleges([]);
    setOriginalColleges([]);
    setCollegeSearch('');
    setShowCollegeDropdown(false);
  };

  const addCollegeToTemp = (col) => {
    if (!tempColleges.find(c => c._id === col._id))
      setTempColleges(prev => [...prev, { _id: col._id, name: col.name, code: col.code }]);
    setCollegeSearch('');
    setShowCollegeDropdown(false);
  };

  const removeCollegeFromTemp = async (col) => {
    const wasSaved = originalColleges.find(c => c._id === col._id);
    if (wasSaved) {
      const r = await Swal.fire({
        title: `Remove "${col.name}"?`,
        text: 'This college was previously assigned. Removing it will unassign it when you save.',
        icon: 'warning', showCancelButton: true,
        confirmButtonColor: '#e74c3c', cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, remove', cancelButtonText: 'Keep',
      });
      if (!r.isConfirmed) return;
    }
    setTempColleges(prev => prev.filter(c => c._id !== col._id));
  };

  const saveAssignment = async (userId) => {
    try {
      const { data } = await cpApi.put(`/controlpanel/admin/${userId}`, {
        colleges: tempColleges.map(c => c._id),
      });
      setUsers(prev => prev.map(u => u._id === userId ? data.data : u));
      setAssignUserId(null);
      setTempColleges([]);
      setOriginalColleges([]);
      await loadData();
      Swal.fire('Saved!', 'College assignments updated.', 'success');
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed.', 'error');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/html'));
    if (dragIndex === dropIndex) return;

    const newColleges = [...tempColleges];
    const draggedItem = newColleges[dragIndex];
    newColleges.splice(dragIndex, 1);
    newColleges.splice(dropIndex, 0, draggedItem);
    setTempColleges(newColleges);
  };

  // ── Filters ──────────────────────────────────────────────────────────────────
  const availableColleges = useMemo(() => {
    const tempIds = tempColleges.map(c => c._id);
    const q = collegeSearch.toLowerCase();
    return colleges.filter(c =>
      !tempIds.includes(c._id) &&
      (c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q))
    );
  }, [colleges, tempColleges, collegeSearch]);

  const displayedUsers = useMemo(() => {
    if (!searchQuery) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(u =>
      u.name.toLowerCase().includes(q) || u.userId?.toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  return (
    <div className="add-admin-in-control-panel-container">
      <h2 className="add-admin-in-control-panel-title">Manage Admin</h2>

      {/* Controls */}
      <div className="add-admin-in-control-panel-controls">
        <div className="add-admin-in-control-panel-search-wrapper">
          <FaSearch className="add-admin-in-control-panel-search-icon" />
          <input
            type="text"
            className="add-admin-in-control-panel-search-input"
            placeholder="Search by name or admin ID (email)..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <button
          className="add-admin-in-control-panel-btn add-admin-in-control-panel-btn-add"
          onClick={() => { if (showAddForm) resetForm(); else setShowAddForm(true); }}
        >
          <FaPlus /> {showAddForm ? 'Close Form' : 'Add Admin'}
        </button>
      </div>

      {/* Add / Edit Form */}
      {showAddForm && (
        <div className="add-admin-in-control-panel-inline-form">
          <h3 className="add-admin-in-control-panel-form-title">
            <FaUserShield style={{ marginRight: 8, color: '#3498db' }} />
            {editId ? 'Edit Admin' : 'Create New Admin'}
          </h3>
          <div className="add-admin-in-control-panel-form-grid">
            <div className="add-admin-in-control-panel-form-group">
              <label>Admin Name <span className="add-admin-in-control-panel-req">*</span></label>
              <input type="text" name="name" value={form.name} onChange={handleInputChange} placeholder="e.g. John Doe" />
            </div>
            <div className="add-admin-in-control-panel-form-group">
              <label>Admin ID (Email) <span className="add-admin-in-control-panel-req">*</span></label>
              <input
                type="text" name="userId" value={form.userId} onChange={handleInputChange}
                placeholder="Unique Admin ID or Email"
              />
            </div>
            <div className="add-admin-in-control-panel-form-group">
              <label>
                Password
                {editId
                  ? <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 400, marginLeft: 4 }}>(leave blank to keep)</span>
                  : <span className="add-admin-in-control-panel-req"> *</span>}
              </label>
              <input
                type="password" name="password" value={form.password} onChange={handleInputChange}
                placeholder={editId ? 'New password to change' : 'Enter password'}
              />
            </div>
          </div>
          <div className="add-admin-in-control-panel-form-buttons">
            <button className="add-admin-in-control-panel-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
            <button className="add-admin-in-control-panel-btn-confirm" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editId ? 'Update Admin' : 'Create Admin'}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="add-admin-in-control-panel-loading">Loading admins...</div>
      ) : (
        <div className="add-admin-in-control-panel-table-wrapper">
          <table className="add-admin-in-control-panel-table">
            <thead>
              <tr>
                <th className="add-admin-in-control-panel-col-sno">S.No</th>
                <th className="add-admin-in-control-panel-align-left">Admin Name</th>
                <th className="add-admin-in-control-panel-col-userid">Admin ID</th>
                <th className="add-admin-in-control-panel-col-assigned">Assigned Colleges</th>
                <th className="add-admin-in-control-panel-col-status">Status</th>
                <th className="add-admin-in-control-panel-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="add-admin-in-control-panel-empty-row">No admins found.</td>
                </tr>
              ) : displayedUsers.map((user, i) => (
                <React.Fragment key={user._id}>

                  {/* Main row */}
                  <tr className={`add-admin-in-control-panel-main-row ${
                    expandedInfo === user._id || assignUserId === user._id
                      ? 'add-admin-in-control-panel-row-active' : ''
                  }`}>
                    <td className="add-admin-in-control-panel-col-sno">{i + 1}</td>

                    <td className="add-admin-in-control-panel-align-left">
                      <div className="add-admin-in-control-panel-name-wrapper"
                           onClick={() => toggleExpandInfo(user._id)}>
                        <span>{user.name}</span>
                        <button className="add-admin-in-control-panel-expand-btn">
                          {expandedInfo === user._id ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                    </td>

                    <td className="add-admin-in-control-panel-col-userid">
                      <span className="add-admin-in-control-panel-badge-id">{user.userId}</span>
                    </td>

                    <td className="add-admin-in-control-panel-col-assigned">
                      {(user.colleges?.length > 0)
                        ? <span className="add-admin-in-control-panel-badge-count">
                            {user.colleges.length} {user.colleges.length === 1 ? 'College' : 'Colleges'}
                          </span>
                        : <span className="add-admin-in-control-panel-not-assigned">Not Assigned</span>}
                    </td>

                    <td className="add-admin-in-control-panel-col-status">
                      <label className="add-admin-in-control-panel-switch">
                        <input type="checkbox" checked={user.isActive} onChange={() => toggleUserStatus(user)} />
                        <span className="add-admin-in-control-panel-slider" />
                      </label>
                      <div className="add-admin-in-control-panel-status-label" style={{ color: user.isActive ? '#27ae60' : '#e74c3c' }}>
                        {user.isActive ? 'Active' : 'Disabled'}
                      </div>
                    </td>

                    <td className="add-admin-in-control-panel-col-actions">
                      <div className="add-admin-in-control-panel-action-btns">
                        <button
                          className={`add-admin-in-control-panel-btn-assign ${assignUserId === user._id ? 'active' : ''}`}
                          onClick={e => { e.stopPropagation(); assignUserId === user._id ? closeAssign() : openAssign(user); }}
                          title="Assign Colleges"
                        >
                          <FaGraduationCap />
                          <span className="add-admin-in-control-panel-btn-text">
                            {assignUserId === user._id ? 'Close' : 'Assign'}
                          </span>
                        </button>
                        <button className="add-admin-in-control-panel-btn-edit" onClick={() => handleEdit(user)} title="Edit"><FaEdit /></button>
                        <button className="add-admin-in-control-panel-btn-delete" onClick={() => handleDelete(user)} title="Delete"><FaTrash /></button>
                      </div>
                    </td>
                  </tr>

                  {/* ── Expand info row (Responsive Breakpoints Handled via CSS) ── */}
                  {expandedInfo === user._id && (
                    <tr className="add-admin-in-control-panel-info-row">
                      <td colSpan="6">
                        <div className="add-admin-in-control-panel-expanded-content">
                          <div className="add-admin-in-control-panel-expanded-grid">
                            
                            {/* Dynamically shown items based on screen size */}
                            <div className="add-admin-in-control-panel-exp-item add-admin-in-control-panel-exp-userid">
                              <span className="add-admin-in-control-panel-exp-label">Admin ID (Email)</span>
                              <span className="add-admin-in-control-panel-exp-value">
                                <span className="add-admin-in-control-panel-badge-id">{user.userId}</span>
                              </span>
                            </div>

                            <div className="add-admin-in-control-panel-exp-item add-admin-in-control-panel-exp-assigned">
                              <span className="add-admin-in-control-panel-exp-label">Assigned Colleges</span>
                              <span className="add-admin-in-control-panel-exp-value">
                                {(user.colleges?.length > 0)
                                  ? <span className="add-admin-in-control-panel-badge-count">
                                      {user.colleges.length} {user.colleges.length === 1 ? 'College' : 'Colleges'}
                                    </span>
                                  : <span className="add-admin-in-control-panel-not-assigned">Not Assigned</span>}
                              </span>
                            </div>

                            <div className="add-admin-in-control-panel-exp-item add-admin-in-control-panel-exp-status">
                              <span className="add-admin-in-control-panel-exp-label">Status</span>
                              <span className="add-admin-in-control-panel-exp-value" style={{ color: user.isActive ? '#27ae60' : '#e74c3c', fontWeight: 700 }}>
                                {user.isActive ? 'Active' : 'Disabled'}
                              </span>
                            </div>

                            <div className="add-admin-in-control-panel-exp-item add-admin-in-control-panel-exp-actions">
                              <span className="add-admin-in-control-panel-exp-label">Actions</span>
                              <div className="add-admin-in-control-panel-action-btns" style={{ justifyContent: 'flex-start', marginTop: '4px' }}>
                                <button
                                  className={`add-admin-in-control-panel-btn-assign ${assignUserId === user._id ? 'active' : ''}`}
                                  onClick={e => { e.stopPropagation(); assignUserId === user._id ? closeAssign() : openAssign(user); }}
                                >
                                  <FaGraduationCap /> Assign
                                </button>
                                <button className="add-admin-in-control-panel-btn-edit" onClick={() => handleEdit(user)}><FaEdit /></button>
                                <button className="add-admin-in-control-panel-btn-delete" onClick={() => handleDelete(user)}><FaTrash /></button>
                              </div>
                            </div>

                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* ── Assignment panel ── */}
                  {assignUserId === user._id && (
                    <tr className="add-admin-in-control-panel-assign-row">
                      <td colSpan="6">
                        <div className="add-admin-in-control-panel-assignment-panel">

                          <div className="add-admin-in-control-panel-panel-header">
                            <div className="add-admin-in-control-panel-panel-title">
                              <FaGraduationCap style={{ color: '#3498db' }} />
                              <span>Assign Colleges to <strong>{user.name}</strong></span>
                              <span className="add-admin-in-control-panel-panel-count">{tempColleges.length} selected</span>
                            </div>
                            <div className="add-admin-in-control-panel-panel-actions">
                              <button className="add-admin-in-control-panel-btn-save" onClick={() => saveAssignment(user._id)}>
                                <FaSave /> Save
                              </button>
                              <button className="add-admin-in-control-panel-btn-cancel-panel" onClick={closeAssign}>
                                <FaTimes /> Cancel
                              </button>
                            </div>
                          </div>

                          <div className="add-admin-in-control-panel-search-section" ref={dropdownRef}>
                            <label className="add-admin-in-control-panel-search-label">Search and add colleges</label>
                            <div className="add-admin-in-control-panel-search-box">
                              <FaSearch className="add-admin-in-control-panel-search-icon" />
                              <input
                                type="text"
                                placeholder="Type college name or code..."
                                value={collegeSearch}
                                onChange={e => { setCollegeSearch(e.target.value); setShowCollegeDropdown(true); }}
                                onFocus={() => setShowCollegeDropdown(true)}
                              />
                              {collegeSearch && (
                                <button className="add-admin-in-control-panel-search-clear"
                                  onClick={() => { setCollegeSearch(''); setShowCollegeDropdown(false); }}>
                                  <FaTimes />
                                </button>
                              )}
                            </div>

                            {showCollegeDropdown && (
                              <div className="add-admin-in-control-panel-dropdown">
                                {availableColleges.length > 0
                                  ? availableColleges.map(col => (
                                    <div key={col._id} className="add-admin-in-control-panel-dropdown-item" onClick={() => addCollegeToTemp(col)}>
                                      <div className="add-admin-in-control-panel-dropdown-name">{col.name}</div>
                                      <div className="add-admin-in-control-panel-dropdown-code">{col.code}</div>
                                    </div>
                                  ))
                                  : <div className="add-admin-in-control-panel-dropdown-empty">
                                      {collegeSearch ? 'No matching colleges.' : 'All active colleges already selected.'}
                                    </div>
                                }
                              </div>
                            )}
                          </div>

                          <div className="add-admin-in-control-panel-selected-section">
                            <div className="add-admin-in-control-panel-selected-label">
                              Selected Colleges
                              {tempColleges.length > 0 && (
                                <span className="add-admin-in-control-panel-selected-count">{tempColleges.length}</span>
                              )}
                            </div>
                            {tempColleges.length > 0 ? (
                              <div className="add-admin-in-control-panel-chips">
                                {tempColleges.map((col, index) => (
                                  <div 
                                    key={col._id} 
                                    className={`add-admin-in-control-panel-chip ${originalColleges.find(c => c._id === col._id) ? 'add-admin-in-control-panel-chip-saved' : 'add-admin-in-control-panel-chip-new'}`}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, index)}
                                    style={{ cursor: 'move' }}
                                  >
                                    <div className="add-admin-in-control-panel-chip-number">{index + 1}</div>
                                    <div className="add-admin-in-control-panel-chip-info">
                                      <span className="add-admin-in-control-panel-chip-name">{col.name}</span>
                                      <span className="add-admin-in-control-panel-chip-code">{col.code}</span>
                                    </div>
                                    <button className="add-admin-in-control-panel-chip-remove" onClick={() => removeCollegeFromTemp(col)} title="Remove">
                                      <FaTimes />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="add-admin-in-control-panel-no-selection">
                                No colleges selected. Search above to add colleges.
                              </div>
                            )}
                          </div>

                        </div>
                      </td>
                    </tr>
                  )}

                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AddAdmin;